import { Component, OnInit } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import { TractorService } from '../stores/tractor.service';
import { ThirdpartySystemResourceTypeFields, Tracker, Tractor, User } from 'shared';
import { UserDomainService } from '../user-domains/user-domain.service';
import { UserDomain } from '../user-domains/user-domain';
import { WebsocketService } from '../services/websocket/websocket.service';
import { TrackerService } from '../stores/tracker.service';
import tractorIcon from '@iconify/icons-mdi/tractor';
import { merge } from 'lodash';
import { ThirdpartySystemService } from '../stores/thirdparty-system.service';

@Component({
  selector: 'app-tractors-grid',
  templateUrl: './tractors-grid.component.html',
  styleUrls: ['./tractors-grid.component.scss']
})
export class TractorsGridComponent implements OnInit {

  tractorIcon = tractorIcon;

  ds: DataSource;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  canAssignTracker: boolean;
  canListThirdPartSystems: boolean;
  canEditThirdPartSystems: boolean;
  /*  domains: UserDomain[];
    selectedDomain?: UserDomain;
    hasSingleDomain: boolean;*/

  availableTrackers: Tracker[];
  isNew: boolean;

  systemFieldsObject = 'thirdparty_systems';
  systemColumns = [];
  isSubContract: boolean;
  minPOI: number;

  constructor(
    private tractorService: TractorService,
    private trackerService: TrackerService,
    // private userDomainsService: UserDomainService,
    public user: User,
    private ws: WebsocketService,
    private thirdpartySystemService: ThirdpartySystemService,
  ) {
    // Data source
    this.ds = tractorService.newDataSource();

    // Permissions
    this.canEdit = user.can('edit tractors');
    this.canAdd = user.can('add tractors');
    this.canDelete = user.can('delete tractors');
    this.canAssignTracker = user.can('assign trackers');
    this.canListThirdPartSystems = user.can('list thirdparty_systems');
    this.canEditThirdPartSystems = user.can('edit thirdparty_systems');

    // Get custom data
    this.ds.store().on('loaded', (result) => {
      if (!result.hasOwnProperty('custom') || !result.custom) {
        return;
      }
      if (!this.canListThirdPartSystems && !this.canEditThirdPartSystems) {
        return;
      }
      this.systemColumns = result.custom.thirdparty_systems_fields.map((d) => {
        return new ThirdpartySystemResourceTypeFields(d);
      }).reduce((all, current) => {
        all.push(...current.toColumns(this, this.systemFieldsObject));
        return all;
      }, []);
    });

    // User domains
    /*    this.domains = userDomainsService.domains;
        this.selectedDomain = userDomainsService.selectedDomain();
        this.hasSingleDomain = userDomainsService.hasSingleDomain();*/

    // Bindings
    this.loadTrackers = this.loadTrackers.bind(this);
    this.codeValidation = this.codeValidation.bind(this);
    // Load available trackers
    this.loadTrackers();
  }

  ngOnInit(): void {
  }

  displayImei(cellInfo: any): string {
    return !!cellInfo.valueText ? cellInfo.valueText : 'Indisponbile';
  }

  loadTrackers() {
    // Available trackers list
    const trackersDs = this.trackerService.newDataSource({
      paginate: false,
    });
    trackersDs.filter(['domain_name', '=', null]);
    trackersDs.load().then(t => {
      this.availableTrackers = t;
    });
  }

  onEditingStart(e: any) {
    this.isNew = false;
    this.isSubContract = e.data.is_subcontract;
    
    // Ensure reconstruction parameters are properly loaded from the data
    // DevExtreme should handle this automatically, but we ensure the data is available
    const reconstructionFields = [
      'reconstruction_min_work_duration_minutes',
      'reconstruction_max_off_duration_minutes',
      'reconstruction_max_idle_duration_minutes',
      'reconstruction_min_intervention_area_m2',
      'reconstruction_min_path_length_meters',
      'reconstruction_min_working_speed_percent',
      'reconstruction_min_field_coverage_percent'
    ];
    
    // Ensure all reconstruction fields exist in the data (even if null)
    reconstructionFields.forEach(field => {
      if (!(field in e.data)) {
        e.data[field] = null;
      }
    });
  }

  onInitNewRow(e: any) {
    this.isNew = true;
    this.isSubContract = false;
    e.data.settings = { minimum_poi: 0 };
    
    // Initialize reconstruction parameters to null for new rows
    e.data.reconstruction_min_work_duration_minutes = null;
    e.data.reconstruction_max_off_duration_minutes = null;
    e.data.reconstruction_max_idle_duration_minutes = null;
    e.data.reconstruction_min_intervention_area_m2 = null;
    e.data.reconstruction_min_path_length_meters = null;
    e.data.reconstruction_min_working_speed_percent = null;
    e.data.reconstruction_min_field_coverage_percent = null;
  }

  editPopup(e: any) {
    // e.component.content().classList.add('tractor-edit-popup');
  }

  codeValidation(params: any) {
    return this.tractorService.checkCode(params.value, params.data?.id).toPromise();
  }

  onRowUpdating(e: any) {
    e.newData[this.systemFieldsObject] = merge(e.oldData[this.systemFieldsObject], e.newData[this.systemFieldsObject] || {});
    
    // Normalize reconstruction parameters: convert 0, empty string, or undefined to null
    this.normalizeReconstructionParams(e.newData);
  }

  onRowInserting(e: any) {
    // Normalize reconstruction parameters: convert 0, empty string, or undefined to null
    this.normalizeReconstructionParams(e.data);
  }

  private normalizeReconstructionParams(data: any) {
    const reconstructionFields = [
      'reconstruction_min_work_duration_minutes',
      'reconstruction_max_off_duration_minutes',
      'reconstruction_max_idle_duration_minutes',
      'reconstruction_min_intervention_area_m2',
      'reconstruction_min_path_length_meters',
      'reconstruction_min_working_speed_percent',
      'reconstruction_min_field_coverage_percent'
    ];

    reconstructionFields.forEach(field => {
      const value = data[field];
      // Convert empty string, undefined, or null to null (which will be sent as null to backend)
      // Note: We keep 0 as 0, but for these parameters, 0 doesn't make sense, so we convert it to null
      if (value === '' || value === undefined || value === null || (typeof value === 'number' && isNaN(value))) {
        data[field] = null;
      } else if (value === 0) {
        // For reconstruction parameters, 0 is not a valid value, convert to null
        data[field] = null;
      }
    });
  }

  checkCode(params: any, system: string, field: string) {
    return this.thirdpartySystemService.checkUnique('TRAILER', {
      system,
      field,
      value: params.value,
      id: params.data?.id,
    }).toPromise();
  }

  subContractChange(e: any, cellInfo) {
    cellInfo.setValue(e.value);
    this.isSubContract = e.value;
  }
}
